import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  FormControlLabel,
  Checkbox,
  Slider,
  CircularProgress,
  Alert,
  MenuItem,
  IconButton,
  FormControl,
  FormLabel,
  InputAdornment,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useTheme } from '@mui/material/styles';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import {
  GlassContainer,
  GlassCard,
  GlassTextField,
  GlassButton,
  GlassAlert,
  GlassColors,
  ButtonTertiary,
  GlassCategoryCard,
  NeumorphicButton,
  ButtonTertiaryIcon,
  GlassShadows,
} from '../../components/glassmorphism/GlassmorphismStyles';
import { useDropzone } from 'react-dropzone';
import DeleteIcon from '@mui/icons-material/Delete';
import { getAllCategories } from '../Categories/actions';
import BrowseHandymanList from '../BrowseHandyman/BrowseHandymanList';
import GoogleMapsWrapper from '../UserProfile/components/UserDetails/GoogleMapsWrapper';
import { connect } from 'react-redux';
import {
  CategoryAttributeViewModel,
  CreateOrderCommand,
  CategoryViewModel,
} from '../../configuration/api/apiSchema';
import {
  createOrder,
  fetchCategoryAttributes,
  sendOrderProposal,
  clearCreatedOrder,
} from './actions';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { trackEvent } from '../../analytics';
import { Autocomplete } from '@react-google-maps/api';
import RailStepper from '../../components/RailStepper/RailStepper';
import MainCategoriesGrid from '../Categories/MainCategoriesGrid';
import { getCategoryIcon } from '../../configuration/categoryConfigIcon';
import { ReactComponent as CloseIcon } from '../../assets/icons/nav/close.svg';
import { getCategoryIconComponent } from '../../helpers/getCategoryIcon';
import MainCategoriesGridSkeleton from '../Categories/skeletons/MainCategoriesGridSkeleton';
import BrowseHandymanSkeleton from '../BrowseHandyman/skeletons/BrowseHandymanSkeleton';

// ----- Types & Enums -----
interface Category {
  key: string;
  name: string;
  subcategories: Category[];
  description?: string;
}

/** Enum for steps */
enum CreateOrderStep {
  SELECT_CATEGORY = 'select-category',
  SELECT_TASK = 'select-task',
  TASK_DESCRIPTION = 'task-description',
}

type DueDateOption =
  | 'specific-date'
  | 'within-3-days'
  | 'this-week'
  | 'flexible';

// type PriceType = 'fixed' | 'hourly' | null;

type SearchResultItem = {
  rootKey: string;
  rootName: string;
  subCategoryKey?: string;
  subCategoryName?: string;
  serviceKey?: string;
  serviceName: string;
  description?: string;
  level: 'root' | 'subcategory' | 'service';
};

// Categories will be loaded from backend

// Transform CategoryViewModel to Category interface
const transformCategoryViewModel = (
  categories: CategoryViewModel[] | null,
): Category[] => {
  if (!categories) return [];

  return categories.map((cat) => ({
    key: cat.key || '',
    name: cat.name || '',
    subcategories: transformCategoryViewModel(cat.subcategories || []),
    description: cat.description || '',
  }));
};

const findCategoryByKey = (
  categories: Category[],
  key: string | null | undefined,
): Category | null => {
  if (!key) return null;

  for (const category of categories) {
    if (category.key === key) return category;

    const nested = findCategoryByKey(category.subcategories, key);
    if (nested) return nested;
  }

  return null;
};
// For image preview (similar to your portfolio component)
interface FileWithPreview {
  src: string;
  file?: File;
}

// ----- Redux Props -----
interface StateFromProps {
  attributes: CategoryAttributeViewModel[] | null;
  isFetchingAttributes: boolean;
  createOrderLoading: boolean;
  createOrderError: string | null;
  createdOrder: any; // Use your OrderViewModel type if available
  allCategories: CategoryViewModel[] | null;
  isFetchingCategories: boolean;
  categoriesError: string | null;
}

interface DispatchFromProps {
  fetchCategoryAttributes: (categoryKey: string) => void;
  createOrder: (orderData: CreateOrderCommand) => void;
  sendOrderProposal: (orderId: string, handymanId: string) => void;
  getAllCategories: () => void;
  clearCreatedOrder: () => void;
}
type SelectedHandyman = {
  id: string;
  name: string;
  categories: string[];
  city?: string;
  cityDistrict?: string;
};
type Props = StateFromProps & DispatchFromProps;

const getEndOfDayUtcIso = (date: Date) => {
  const d = new Date(date);
  d.setHours(23, 59, 0, 0);
  return d.toISOString();
};

const getComputedDueDateUtc = (
  option: DueDateOption | null,
  specificDate: string,
): string | null => {
  if (!option) return null;

  const now = new Date();

  switch (option) {
    case 'specific-date': {
      if (!specificDate) return null;
      return new Date(specificDate).toISOString();
    }

    case 'within-3-days': {
      const date = new Date(now);
      date.setDate(date.getDate() + 3);
      return getEndOfDayUtcIso(date);
    }

    case 'this-week': {
      const date = new Date(now);
      const day = date.getDay(); // 0 = Sunday, 1 = Monday ...
      const daysUntilSunday = day === 0 ? 0 : 7 - day;
      date.setDate(date.getDate() + daysUntilSunday);
      return getEndOfDayUtcIso(date);
    }

    case 'flexible':
      const date = new Date(now);
      date.setDate(date.getDate() + 45);
      return getEndOfDayUtcIso(date);

    default:
      return null;
  }
};

// ----- Component -----
const CreateOrderPage: React.FC<Props> = (props: Props) => {
  // Theme mode
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const navigate = useNavigate();
  const location = useLocation();

  type CreateOrderLocationState = {
    handyman: SelectedHandyman;
    selectedCategoryKeys?: string[];
  };

  const routeState = (location.state ?? {}) as CreateOrderLocationState;

  const handyman = routeState.handyman;
  const handymanId = handyman?.id;
  const handymanName = handyman?.name;
  const handymanCategories = handyman?.categories ?? [];
  const selectedCategoryKeys = routeState.selectedCategoryKeys ?? [];
  const initialAddress = routeState.handyman?.city ?? '';

  const {
    attributes,
    isFetchingAttributes,
    createOrderLoading,
    createdOrder,
    createOrderError,
    allCategories,
    isFetchingCategories,
    categoriesError,
  } = props;

  // Wizard state
  const [currentStep, setCurrentStep] = useState<CreateOrderStep>(
    CreateOrderStep.SELECT_CATEGORY,
  );
  const [selectedCategoryKey, setSelectedCategoryKey] = useState<
    string | undefined
  >(undefined);

  const [taskName, setTaskName] = useState('');
  const [taskDescription, setTaskDescription] = useState('');

  const [price, setPrice] = useState('');
  // const [priceType, setPriceType] = useState<PriceType>(null);
  const [photos, setPhotos] = useState<FileWithPreview[]>([]);
  const [categorySearch, setCategorySearch] = useState('');
  const [selectedSearchResult, setSelectedSearchResult] =
    useState<SearchResultItem | null>(null);
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  // Local state to store dynamic attribute input values
  const [attributeValues, setAttributeValues] = useState<{
    [key: string]: any;
  }>({});
  // Flag to indicate that order has been created successfully
  const [orderCreated, setOrderCreated] = useState(false);
  // State for form error messages (used for validations)
  const [formError, setFormError] = useState('');

  const [selectedHandymanId, setSelectedHandymanId] = useState<string | null>(
    null,
  );
  const [sendingProposal, setSendingProposal] = useState(false);
  const [sentProposalIds, setSentProposalIds] = useState<string[]>([]);
  const [orderCreationNotification, setOrderCreationNotification] =
    useState(true);

  const [address, setAddress] = useState(initialAddress);
  const [autocompleteRef, setAutocompleteRef] =
    useState<google.maps.places.Autocomplete | null>(null);

  const [handymanFilterAddress, setHandymanFilterAddress] = useState<
    string | null
  >(null);
  const [handymanRadiusKm, setHandymanRadiusKm] = useState<number>(10);
  const [autocompleteHandymanRef, setAutocompleteHandymanRef] =
    useState<google.maps.places.Autocomplete | null>(null);

  type InviteStatus = 'idle' | 'sent' | 'failed';
  const [inviteStatus, setInviteStatus] = useState<InviteStatus>('idle');
  const [selectedRootCategoryKey, setSelectedRootCategoryKey] = useState<
    string | undefined
  >(undefined);
  const [showHandymanStage, setShowHandymanStage] = useState(false);
  const steps = [
    CreateOrderStep.SELECT_CATEGORY,
    CreateOrderStep.SELECT_TASK,
    CreateOrderStep.TASK_DESCRIPTION,
  ];
  const hasPreselectedHandyman = !!handymanId;
  const hasSinglePreselectedCategory = selectedCategoryKeys.length === 1;
  const shouldStartFromTaskDescription =
    hasPreselectedHandyman && hasSinglePreselectedCategory;

  const shouldShowBrowseHandyman =
    showHandymanStage && (!hasPreselectedHandyman || inviteStatus === 'failed');

  const shouldShowInviteResultOnly =
    showHandymanStage && hasPreselectedHandyman && inviteStatus === 'sent';

  useEffect(() => {
    if (!shouldStartFromTaskDescription) return;

    const singleServiceKey = selectedCategoryKeys[0];
    if (!singleServiceKey) return;

    setSelectedCategoryKey(singleServiceKey);
    setCurrentStep(CreateOrderStep.TASK_DESCRIPTION);
  }, [shouldStartFromTaskDescription, selectedCategoryKeys]);
  // Track when user starts create order flow
  useEffect(() => {
    trackEvent('create_order_started');
  }, []);

  /** Toggle a single category's checked state */
  const toggleCategory = (catKey: string, isLeaf: boolean) => {
    console.log('🔍 toggleCategory called:', { catKey, isLeaf });

    // Only allow leaf categories to be selected
    if (!isLeaf) {
      console.log('❌ Not a leaf category, showing error');
      setFormError(
        'Please select a specific service category (not a parent category).',
      );
      return;
    }

    console.log('✅ Leaf category selected:', catKey);

    // Clear previous attributes if switching categories
    if (selectedCategoryKey !== catKey) {
      console.log('🔄 Clearing previous attributes');
      setAttributeValues({});
    }

    setSelectedCategoryKey(catKey);
    setFormError('');

    // Fetch attributes immediately when a leaf category is selected
    console.log('📡 Fetching attributes for category:', catKey);
    props.fetchCategoryAttributes(catKey);
  };

  // Dropzone logic for images
  const { getRootProps, getInputProps } = useDropzone({
    accept: { 'image/*': [] },
    multiple: true,
    onDrop: async (acceptedFiles) => {
      const base64Files = await Promise.all(
        acceptedFiles.map((file) =>
          fileToBase64(file).then((base64) => ({
            src: base64,
            file,
          })),
        ),
      );
      setPhotos((prev) => [...prev, ...base64Files]);
    },
  });

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });
  };

  const handleReset = () => {
    setCurrentStep(CreateOrderStep.SELECT_CATEGORY);
    setSelectedCategoryKey(undefined);
    setTaskName('');
    setTaskDescription('');
    setDueDateOption(null);
    setSpecificDueDate('');
    setPrice('');
    setPhotos([]);
    setAttributeValues({});
    setOrderCreated(false);
    setFormError('');
    setSelectedHandymanId(null);
    setSendingProposal(false);
    setSelectedRootCategoryKey(undefined);
    setSelectedSubCategoryKey(null);
    setShowHandymanStage(false);
    setSentProposalIds([]);

    // Clear Redux state
    props.clearCreatedOrder();

    // if (shouldNavigate) {
    //   navigate('/orders');
    // }

    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Fetch categories on component mount
  useEffect(() => {
    props.getAllCategories();

    // Cleanup function to clear Redux state when component unmounts
    return () => {
      props.clearCreatedOrder();
    };
  }, []);

  // Clear attributes when going back to category selection step
  useEffect(() => {
    if (currentStep === CreateOrderStep.SELECT_CATEGORY) {
      // Clear attributes to ensure fresh state
      setAttributeValues({});
    }
  }, [currentStep]);

  // When the createdOrder prop is set (and orderCreated flag is false), move to next step

  const canSendInvite =
    !!selectedCategoryKey && handymanCategories.includes(selectedCategoryKey);
  useEffect(() => {
    const handleCreatedOrder = async () => {
      if (!createdOrder || orderCreated) return;

      trackEvent('create_order_completed', {
        order_id: createdOrder.id,
        category_key: selectedCategoryKey,
      });

      setOrderCreated(true);

      if (address) {
        setHandymanFilterAddress(address);
      }

      if (handymanId) {
        if (!canSendInvite) {
          setInviteStatus('failed');
          setShowHandymanStage(true);
          return;
        }

        try {
          await props.sendOrderProposal(createdOrder.id, handymanId);

          trackEvent('customer_invitation_sent', {
            order_id: createdOrder.id,
            handyman_id: handymanId,
          });

          setInviteStatus('sent');
        } catch (error) {
          console.error('Failed to send proposal:', error);
          setInviteStatus('failed');
        }
      }

      setShowHandymanStage(true);

      setTimeout(() => {
        window.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
      }, 100);
    };

    handleCreatedOrder();
  }, [
    createdOrder,
    orderCreated,
    address,
    selectedCategoryKey,
    handymanId,
    handymanCategories,
    props,
  ]);

  const resetCategorySearch = () => {
    setCategorySearch('');
    setSelectedSearchResult(null);
    setIsSearchOpen(false);
  };
  // Handler for moving to next step
  const handleNext = () => {
    setFormError('');

    if (currentStep === CreateOrderStep.SELECT_CATEGORY) {
      if (!selectedRootCategoryKey) {
        setFormError('Please select a category.');
        return;
      }

      setCurrentStep(CreateOrderStep.SELECT_TASK);
      return;
    }

    if (currentStep === CreateOrderStep.SELECT_TASK) {
      if (!selectedCategoryKey) {
        setFormError('Please select a service.');
        return;
      }
      // Attributes are already fetched when category is selected, just move to next step
      setCurrentStep(CreateOrderStep.TASK_DESCRIPTION);
      return;
    }
    if (currentStep === CreateOrderStep.TASK_DESCRIPTION) {
      // Validate static fields
      if (!taskName.trim() || !taskDescription.trim()) {
        setFormError('Task Name and Task Description are required.');
        return;
      }
      if (!dueDateOption) {
        setFormError('Please select when you need it done.');
        return;
      }

      if (!address) {
        setFormError('Address field is required.');
        return;
      }

      if (!price || Number(price) <= 0) {
        setFormError('Price is required and must be greater than 0.');
        return;
      }

      if (dueDateOption === 'specific-date' && !specificDueDate) {
        setFormError('Please select a specific date.');
        return;
      }
      // Create payload for order creation
      const orderPayload: CreateOrderCommand = {
        categoryKey: selectedCategoryKey,
        name: taskName,
        description: taskDescription,
        address: address,
        location: address,
        orderAttributeAnswers: attributes
          ? attributes.map((attr) => ({
              categoryAttributeId: attr.id || '',
              value: attributeValues[attr.id || '']
                ? String(attributeValues[attr.id || ''])
                : '',
            }))
          : [],
        dueDateUtc: getComputedDueDateUtc(dueDateOption, specificDueDate),
        price: price ? Number(price) : null,
        base64Images:
          photos.length > 0 ? photos.map((photo) => photo.src) : null,
      };
      // Dispatch the create order action
      props.createOrder(orderPayload);
    }
  };

  // Handler for moving back
  const handleBack = () => {
    if (orderCreated) return; // Disallow going back after order creation
    const currentIndex = steps.indexOf(currentStep);
    if (currentIndex > 0) {
      setCurrentStep(steps[currentIndex - 1]);
      setFormError('');
    }
    resetCategorySearch();
  };

  // Handler when a handyman is selected
  const onSelectHandyman = async (handymanId: string) => {
    setSelectedHandymanId(handymanId);
    setSendingProposal(true);
    try {
      trackEvent('customer_invitation_sent', {
        order_id: createdOrder.id,
        handyman_id: handymanId,
      });
      // Send proposal to the selected handyman
      await props.sendOrderProposal(createdOrder.id, handymanId);
      // Clear Redux state and reset component
      // props.clearCreatedOrder();
      setSentProposalIds((prev) =>
        prev.includes(handymanId) ? prev : [...prev, handymanId],
      );
      setSendingProposal(false);
      // handleReset();
    } catch (error) {
      console.error('Failed to send proposal:', error);
    } finally {
      setSendingProposal(false);
    }
  };

  const handleBrowseHandymanAction = (handyman?: SelectedHandyman) => {
    if (!handyman?.id) return;

    void onSelectHandyman(handyman.id);
  };

  // Render dynamic attribute input based on the attribute's input type.
  const renderAttributeInput = (attr: CategoryAttributeViewModel) => {
    const attrId = attr.id || '';
    const label = attr.translatedAtributeContent?.label || 'Attribute';
    const defaultValue = attr.translatedAtributeContent?.defaultValue || '';
    const value = attributeValues[attrId] ?? defaultValue;

    switch (attr.inputType) {
      case 'Text':
        return (
          <TextField
            fullWidth
            variant='outlined'
            label={label}
            value={value}
            onChange={(e) =>
              setAttributeValues({
                ...attributeValues,
                [attrId]: e.target.value,
              })
            }
            placeholder={attr.translatedAtributeContent?.placeholder ?? ''}
            sx={{ mb: 2 }}
          />
        );
      case 'TextArea':
        return (
          <TextField
            fullWidth
            variant='outlined'
            multiline
            minRows={4}
            maxRows={10}
            label={label}
            value={value}
            onChange={(e) =>
              setAttributeValues({
                ...attributeValues,
                [attrId]: e.target.value,
              })
            }
            placeholder={attr.translatedAtributeContent?.placeholder ?? ''}
            sx={{ mb: 2 }}
          />
        );
      case 'Checkbox':
        return (
          <FormControlLabel
            control={
              <Checkbox
                checked={!!value}
                onChange={(e) =>
                  setAttributeValues({
                    ...attributeValues,
                    [attrId]: e.target.checked,
                  })
                }
              />
            }
            label={label}
            sx={{ mb: 2 }}
          />
        );
      case 'Slider':
        return (
          <Box sx={{ mt: 2, mb: 2 }}>
            <Typography gutterBottom>{label}</Typography>
            <Slider
              value={typeof value === 'number' ? value : 0}
              onChange={(e, newValue) =>
                setAttributeValues({
                  ...attributeValues,
                  [attrId]: newValue as number,
                })
              }
              valueLabelDisplay='auto'
              min={0}
              max={100}
            />
          </Box>
        );
      case 'Dropdown':
        return (
          <TextField
            select
            fullWidth
            variant='outlined'
            label={label}
            value={value}
            onChange={(e) =>
              setAttributeValues({
                ...attributeValues,
                [attrId]: e.target.value,
              })
            }
            sx={{ mb: 2 }}
          >
            <MenuItem value=''>
              <em>Select</em>
            </MenuItem>
            {attr.translatedDropdownOption?.options?.map((option, idx) => (
              <MenuItem key={idx} value={option.value || ''}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        );
      case 'Number':
        return (
          <TextField
            fullWidth
            variant='outlined'
            label={label}
            type='number'
            value={value}
            onChange={(e) =>
              setAttributeValues({
                ...attributeValues,
                [attrId]: e.target.value,
              })
            }
            placeholder={attr.translatedAtributeContent?.placeholder ?? ''}
            sx={{ mb: 2 }}
          />
        );
      default:
        return null;
    }
  };

  const [selectedSubCategoryKey, setSelectedSubCategoryKey] = useState<
    string | null
  >(null);

  useEffect(() => {
    if (selectedCategoryKeys.length !== 1) return;

    const singleServiceKey = selectedCategoryKeys[0];
    if (!singleServiceKey) return;

    setSelectedCategoryKey(singleServiceKey);
    setCurrentStep(CreateOrderStep.TASK_DESCRIPTION);
  }, [selectedCategoryKeys]);

  const categoryTree = transformCategoryViewModel(allCategories);

  const selectedRootCategory = findCategoryByKey(
    categoryTree,
    selectedRootCategoryKey,
  );

  const selectedSubCategory = findCategoryByKey(
    categoryTree,
    selectedSubCategoryKey,
  );

  // Step 2
  const subCategoryOptions = selectedRootCategory?.subcategories ?? [];

  // Step 2
  const serviceOptions = selectedSubCategory?.subcategories ?? [];

  const handleSelectCategory = (category: Category) => {
    setSelectedRootCategoryKey(category.key);
    setSelectedSubCategoryKey(null);
    setSelectedCategoryKey(undefined);
    setAttributeValues({});
    setFormError('');
    setCurrentStep(CreateOrderStep.SELECT_TASK);
    resetCategorySearch();
  };

  const [dueDateOption, setDueDateOption] = useState<DueDateOption | null>(
    null,
  );
  const [specificDueDate, setSpecificDueDate] = useState('');
  // TODO price field with price type
  // const handlePriceTypeChange = (type: Exclude<PriceType, null>) => {
  //   setPriceType(type);
  //   setPrice('');
  // };

  const normalizeText = (value: string) => value.trim().toLowerCase();

  const searchInCategoryTree = (
    categories: Category[],
    query: string,
  ): Array<{
    rootKey: string;
    rootName: string;
    subCategoryKey?: string;
    subCategoryName?: string;
    serviceKey?: string;
    serviceName: string;
    description?: string;
    level: 'root' | 'subcategory' | 'service';
  }> => {
    const normalizedQuery = normalizeText(query);

    if (!normalizedQuery) return [];

    const results: Array<{
      rootKey: string;
      rootName: string;
      subCategoryKey?: string;
      subCategoryName?: string;
      serviceKey?: string;
      serviceName: string;
      description?: string;
      level: 'root' | 'subcategory' | 'service';
    }> = [];

    categories.forEach((root) => {
      const rootMatches =
        normalizeText(root.name).includes(normalizedQuery) ||
        normalizeText(root.description || '').includes(normalizedQuery);

      if (rootMatches) {
        results.push({
          rootKey: root.key,
          rootName: root.name,
          serviceName: root.name,
          description: root.description,
          level: 'root',
        });
      }

      root.subcategories.forEach((sub) => {
        const subMatches =
          normalizeText(sub.name).includes(normalizedQuery) ||
          normalizeText(sub.description || '').includes(normalizedQuery);

        if (subMatches) {
          results.push({
            rootKey: root.key,
            rootName: root.name,
            subCategoryKey: sub.key,
            subCategoryName: sub.name,
            serviceName: sub.name,
            description: sub.description,
            level: 'subcategory',
          });
        }

        sub.subcategories.forEach((service) => {
          const serviceMatches =
            normalizeText(service.name).includes(normalizedQuery) ||
            normalizeText(service.description || '').includes(normalizedQuery);

          if (serviceMatches) {
            results.push({
              rootKey: root.key,
              rootName: root.name,
              subCategoryKey: sub.key,
              subCategoryName: sub.name,
              serviceKey: service.key,
              serviceName: service.name,
              description: service.description,
              level: 'service',
            });
          }
        });
      });
    });

    return results;
  };

  const searchResults = useMemo(() => {
    return searchInCategoryTree(categoryTree, categorySearch);
  }, [categoryTree, categorySearch]);
  const applySearchResult = (result: SearchResultItem) => {
    setFormError('');
    setAttributeValues({});
    setSelectedRootCategoryKey(result.rootKey);

    if (result.level === 'root') {
      setSelectedSubCategoryKey(null);
      setSelectedCategoryKey(undefined);
      setCurrentStep(CreateOrderStep.SELECT_TASK);
      setIsSearchOpen(false);
      return;
    }

    if (result.level === 'subcategory') {
      setSelectedSubCategoryKey(result.subCategoryKey || null);
      setSelectedCategoryKey(undefined);
      setCurrentStep(CreateOrderStep.SELECT_TASK);
      setIsSearchOpen(false);
      return;
    }

    if (result.level === 'service') {
      setSelectedSubCategoryKey(result.subCategoryKey || null);
      setSelectedCategoryKey(result.serviceKey);
      setCurrentStep(CreateOrderStep.SELECT_TASK);
      setIsSearchOpen(false);

      if (result.serviceKey) {
        props.fetchCategoryAttributes(result.serviceKey);
      }

      resetCategorySearch();
      return;
    }
  };
  const handleSearchResultSelect = (result: SearchResultItem) => {
    setSelectedSearchResult(result);
    setCategorySearch(result.serviceName);
    applySearchResult(result);
  };
  const handleCategorySearchSubmit = () => {
    if (!categorySearch.trim()) {
      setFormError('Please enter a category name.');
      return;
    }

    const resultToApply = selectedSearchResult || searchResults[0];

    if (!resultToApply) {
      setFormError('No matching categories found.');
      return;
    }

    applySearchResult(resultToApply);
  };

  const handleCategorySearchChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setCategorySearch(e.target.value);
    setSelectedSearchResult(null);
    setIsSearchOpen(true);
  };
  const getActionBtnTitle = (handymanId: string): string => {
    if (sentProposalIds.includes(handymanId)) {
      return 'Proposal sent';
    }

    if (sendingProposal && selectedHandymanId === handymanId) {
      return 'Sending...';
    }

    return 'Send Proposal';
  };

  return (
    <GlassContainer>
      <Box
        width={{
          xs: '100%',
          sm: '95%',
          tablet: '740px',
          md: '860px',
          lg: '956px',
        }}
        mx='auto'
        sx={{ p: { xs: 0, sm: '24px 0' } }}
      >
        <GlassCard
          sx={{
            p: {
              xs: '24px 12px',
              tablet: '32px 24px',
              md: '32px 50px',
              lg: '32px 98px',
            },
            boxShadow: {
              xs: 'none',
              sm: GlassShadows.cardShadowDefault,
            },
            borderRadius: { sm: '32px', tablet: '64px' },
          }}
        >
          <ButtonTertiaryIcon
            onClick={() => navigate(-1)}
            sx={{
              position: 'absolute',
              zIndex: 1,
              top: { xs: '8px', sm: '24px', tablet: '32px' },
              right: { xs: '8px', sm: '24px', tablet: '32px' },
              width: { xs: '32px', tablet: '40px' },
              height: { xs: '32px', tablet: '40px' },
            }}
          >
            <CloseIcon />
          </ButtonTertiaryIcon>
          {/* Display form error if exists */}
          {formError && (
            <GlassAlert severity='error' sx={{ mt: 2, mb: 2 }}>
              {formError}
            </GlassAlert>
          )}
          {/* Step Content */}
          {/* {currentStep === CreateOrderStep.SELECT_CATEGORY && (
            <Box sx={{ mt: 4 }}>
              <Typography variant='h6' gutterBottom>
                What do you need help with?
              </Typography>
              {isFetchingCategories && (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                  <CircularProgress />
                </Box>
              )}
              {categoriesError && (
                <Alert severity='error' sx={{ mt: 2, mb: 2 }}>
                  Failed to load categories: {categoriesError}
                </Alert>
              )}
              {allCategories && (
                <CategoryTreeSelect
                  selectedCategoryKeys={
                    selectedCategoryKey
                      ? new Set([selectedCategoryKey])
                      : new Set()
                  }
                  toggleCategory={toggleCategory}
                  isSingleSelect={true}
                  onlyLeafSelectable={true}
                  categories={transformCategoryViewModel(allCategories)}
                />
              )}
              <Typography variant='body2' color='textSecondary' sx={{ mt: 2 }}>
                Selected:{' '}
                {selectedCategoryKey && allCategories
                  ? getCategoryName(
                      transformCategoryViewModel(allCategories),
                      selectedCategoryKey,
                    )
                  : 'None'}
              </Typography> */}
          {/* Show loading indicator when fetching attributes */}
          {/* {isFetchingAttributes && selectedCategoryKey && (
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  <Typography variant='body2' color='textSecondary'>
                    Loading category details...
                  </Typography>
                </Box>
              )}
            </Box>
          )} */}
          {!showHandymanStage && (
            <Box sx={{ mb: 2 }}>
              <Typography
                variant='h4'
                sx={{
                  mb: { xs: '16px', sm: '24px' },
                  p: '0 32px',
                  textAlign: 'center',
                  fontSize: { xs: '20px', sm: '24px' },
                  fontFamily: 'Montserrat, sans-serif',
                  fontWeight: { xs: 800, sm: 700 },
                  lineHeight: { xs: 1.6, sm: '133%' },
                  letterSpacing: { xs: '.15px', sm: 0 },
                  color: isDark ? GlassColors.accent : GlassColors.secondary,
                }}
              >
                {currentStep === CreateOrderStep.TASK_DESCRIPTION
                  ? 'Tell us about your task'
                  : 'What do you need help with?'}
              </Typography>

              {currentStep !== CreateOrderStep.TASK_DESCRIPTION && (
                <Box sx={{ mb: { xs: 0, sm: 3 }, position: 'relative' }}>
                  <GlassTextField
                    fullWidth
                    placeholder='Search categories'
                    value={categorySearch}
                    onChange={handleCategorySearchChange}
                    onFocus={() => {
                      if (categorySearch.trim()) {
                        setIsSearchOpen(true);
                      }
                    }}
                    sx={{
                      '& .MuiInputBase-root': {
                        p: '4px 2px 4px 16px',
                        height: '36px',
                      },
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position='end'>
                          <GlassButton
                            onClick={handleCategorySearchSubmit}
                            disabled={!categorySearch.trim()}
                            sx={{
                              minWidth: 'unset',
                              borderRadius: '999px',
                              '&&': {
                                py: '4px',
                                px: '12px',
                                fontSize: '12px',
                              },
                            }}
                          >
                            Search
                          </GlassButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  {isSearchOpen && categorySearch.trim().length > 0 && (
                    <GlassCard
                      sx={{
                        mt: 1,
                        p: 1,
                        borderRadius: '32px',
                        maxHeight: 360,
                        overflowY: 'auto',
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        zIndex: 20,
                        backgroundColor: isDark
                          ? GlassColors.dark.primaryBackground
                          : GlassColors.light.primaryBackground,
                      }}
                    >
                      {searchResults.length > 0 ? (
                        searchResults.map((result, index) => {
                          const isSelected =
                            selectedSearchResult &&
                            selectedSearchResult.level === result.level &&
                            selectedSearchResult.rootKey === result.rootKey &&
                            selectedSearchResult.subCategoryKey ===
                              result.subCategoryKey &&
                            selectedSearchResult.serviceKey ===
                              result.serviceKey;

                          return (
                            <Box
                              key={`${result.level}-${result.serviceKey || result.subCategoryKey || result.rootKey}-${index}`}
                              onClick={() => handleSearchResultSelect(result)}
                              sx={{
                                p: 1.5,
                                borderRadius: '14px',
                                cursor: 'pointer',
                                backgroundColor: isSelected
                                  ? isDark
                                    ? 'rgba(255,255,255,0.08)'
                                    : 'rgba(0,0,0,0.06)'
                                  : 'transparent',
                                '&:hover': {
                                  backgroundColor: isDark
                                    ? 'rgba(255,255,255,0.06)'
                                    : 'rgba(0,0,0,0.04)',
                                },
                              }}
                            >
                              <Typography sx={{ fontWeight: 700 }}>
                                {result.serviceName}
                              </Typography>

                              <Typography variant='body2' sx={{ opacity: 0.7 }}>
                                {result.rootName}
                                {result.subCategoryName
                                  ? ` → ${result.subCategoryName}`
                                  : ''}
                              </Typography>

                              {result.description && (
                                <Typography
                                  variant='body2'
                                  sx={{
                                    mt: 0.5,
                                    color: isDark ? '#B8B8B8' : '#5E5E5E',
                                  }}
                                >
                                  {result.description}
                                </Typography>
                              )}
                            </Box>
                          );
                        })
                      ) : (
                        <Box sx={{ p: 2 }}>
                          <Typography>No results found</Typography>
                        </Box>
                      )}
                    </GlassCard>
                  )}
                </Box>
              )}
            </Box>
          )}

          {currentStep === CreateOrderStep.SELECT_CATEGORY && (
            <Box sx={{ mb: 2 }}>
              {isFetchingCategories ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                  <MainCategoriesGridSkeleton useGlassCard />
                </Box>
              ) : (
                <Grid
                  container
                  spacing={2}
                  sx={{ maxWidth: { xs: '304px', sm: 'none' }, margin: 'auto' }}
                >
                  <MainCategoriesGrid
                    filteredCategories={categoryTree}
                    handleSelectCategory={handleSelectCategory}
                    useGlassCard
                  />
                </Grid>
              )}

              {categoriesError && (
                <Alert severity='error' sx={{ mt: 2, mb: 2 }}>
                  Failed to load categories: {categoriesError}
                </Alert>
              )}

              {/* {categoryTree.length > 0 && (
                <Grid
                  container
                  spacing={2}
                  sx={{ maxWidth: { xs: '304px', sm: 'none' }, margin: 'auto' }}
                >
                  <MainCategoriesGrid
                    filteredCategories={categoryTree}
                    handleSelectCategory={handleSelectCategory}
                    useGlassCard
                  />
                </Grid>
              )} */}
            </Box>
          )}

          {currentStep === CreateOrderStep.SELECT_TASK &&
            selectedRootCategoryKey && (
              <Box sx={{ mb: 2 }}>
                {selectedRootCategory && !selectedSubCategoryKey && (
                  <Box sx={{ mt: { xs: 0, sm: 4 } }}>
                    <GlassCard
                      sx={{
                        p: { xs: '16px 8px', sm: 3 },
                        borderRadius: '32px',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          mb: { xs: '18px', sm: 4 },
                          flexWrap: 'wrap',
                        }}
                      >
                        <ButtonTertiary
                          variant='outlined'
                          onClick={() => {
                            setCurrentStep(CreateOrderStep.SELECT_CATEGORY);
                            setFormError('');
                            resetCategorySearch();
                          }}
                          sx={{
                            display: { xs: 'none', sm: 'flex' },
                            minWidth: 'unset',
                            px: 2,
                            py: 0.75,
                            borderRadius: '999px',
                          }}
                        >
                          Back
                        </ButtonTertiary>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            mx: 'auto',
                            '&& svg': {
                              width: {
                                xs: '32px !important',
                                sm: '48px !important',
                              },
                              height: {
                                xs: '32px !important',
                                sm: '48px !important',
                              },
                              flexShrink: 0,
                              mr: '16px',
                            },
                          }}
                        >
                          {getCategoryIcon(selectedRootCategory.key)}
                          <Typography
                            sx={{
                              fontFamily: 'Montserrat, sans-serif',
                              fontWeight: 700,
                              fontSize: { xs: '16px', md: '24px' },
                              color: isDark
                                ? GlassColors.dark.textSecondary
                                : GlassColors.light.textSecondary,
                            }}
                          >
                            {selectedRootCategory.name}
                          </Typography>
                        </Box>
                      </Box>

                      <Grid
                        container
                        spacing={{ xs: 1, sm: 2 }}
                        sx={{ justifyContent: 'center' }}
                      >
                        {subCategoryOptions.map((sub) => (
                          <Grid
                            key={sub.key}
                            size={{ xs: 6, mobile: 4, sm: 6, md: 4 }}
                          >
                            <GlassCategoryCard
                              onClick={() => {
                                setSelectedSubCategoryKey(sub.key);
                                setSelectedCategoryKey(undefined);
                                setFormError('');
                              }}
                              sx={{
                                p: { xs: '12px', sm: '12px 24px' },
                                cursor: 'pointer',
                                borderRadius: '20px',
                                display: 'flex',
                                aspectRatio: { xs: 1 / 1, sm: 'unset' },
                                justifyContent: { xs: 'center', sm: 'start' },
                                flexDirection: { xs: 'column', sm: 'row' },
                                alignItems: 'center',
                                gap: { xs: '8px', sm: '12px' },
                                '& svg': {
                                  width: '48px !important',
                                  height: '48px !important',
                                  flexShrink: 0,
                                },
                                backgroundColor: isDark
                                  ? GlassColors.dark.primaryBackground
                                  : GlassColors.light.primaryBackground,
                              }}
                            >
                              {React.createElement(
                                getCategoryIconComponent(sub.key),
                              )}
                              <Typography
                                sx={{
                                  fontFamily: 'Roboto, sans-serif',
                                  fontWeight: 700,
                                  fontSize: { xs: '14px', s: '16px' },
                                  lineHeight: { xs: 1.57, sm: '175%' },
                                  letterSpacing: '.15px',
                                  textAlign: { xs: 'center', sm: 'left' },
                                  color: isDark
                                    ? GlassColors.dark.textSecondary
                                    : GlassColors.light.textSecondary,
                                }}
                              >
                                {sub.name}
                              </Typography>
                            </GlassCategoryCard>
                          </Grid>
                        ))}
                      </Grid>
                      <ButtonTertiary
                        variant='outlined'
                        onClick={() => {
                          setCurrentStep(CreateOrderStep.SELECT_CATEGORY);
                          setFormError('');
                          resetCategorySearch();
                        }}
                        sx={{
                          display: { xs: 'flex', sm: 'none' },
                          minWidth: 'unset',
                          px: 2,
                          py: 0.75,
                          borderRadius: '999px',
                          m: '16px auto 0',
                          '& path': { fill: 'currentColor' },
                        }}
                      >
                        <ArrowBackIosIcon />
                        Back
                      </ButtonTertiary>
                    </GlassCard>
                  </Box>
                )}

                {selectedSubCategory && !!selectedSubCategoryKey && (
                  <Box sx={{ mt: 4 }}>
                    <GlassCard
                      sx={{
                        p: { xs: 2, md: 3 },
                        borderRadius: '32px',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          mb: { xs: 2, sm: 4 },
                          flexWrap: 'wrap',
                        }}
                      >
                        <ButtonTertiary
                          variant='outlined'
                          onClick={() => {
                            setSelectedSubCategoryKey(null);
                            setSelectedCategoryKey(undefined);
                            setFormError('');
                            resetCategorySearch();
                          }}
                          sx={{
                            minWidth: 'unset',
                            px: 2,
                            py: 0.75,
                            borderRadius: '999px',
                            display: { xs: 'none', sm: 'flex' },
                          }}
                        >
                          Back
                        </ButtonTertiary>

                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '16px',
                            mx: 'auto',
                            '& svg': {
                              width: {
                                xs: '32px !important',
                                sm: '48px !important',
                              },
                              height: {
                                xs: '32px !important',
                                sm: '48px !important',
                              },
                            },
                          }}
                        >
                          {React.createElement(
                            getCategoryIconComponent(selectedSubCategory.key),
                          )}
                          <Typography
                            sx={{
                              fontFamily: 'Montserrat, sans-serif',
                              fontSize: { xs: '16px', md: '24px' },
                              fontWeight: 700,
                              lineHeight: { xs: 1.75, sm: 1.33 },
                              color: isDark
                                ? GlassColors.dark.textSecondary
                                : GlassColors.light.textSecondary,
                              mx: 'auto',
                            }}
                          >
                            {selectedSubCategory.name}
                          </Typography>
                        </Box>

                        <GlassButton
                          onClick={handleNext}
                          disabled={createOrderLoading}
                          sx={{
                            opacity: selectedCategoryKey ? 1 : 0,
                            display: { xs: 'none', sm: 'flex' },

                            '&&': {
                              height: '32px',
                              fontSize: '12px',
                              transition: 'box-shadow 0.3s ease, opacity .3s',
                            },
                          }}
                        >
                          {createOrderLoading ? (
                            <CircularProgress size={24} color='inherit' />
                          ) : (
                            'Next'
                          )}
                        </GlassButton>
                      </Box>

                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                        }}
                      >
                        {serviceOptions.map((service) => {
                          const checked = selectedCategoryKey === service.key;

                          return (
                            <Box
                              key={service.key}
                              onClick={() => {
                                setSelectedCategoryKey(service.key);
                                setFormError('');
                                props.fetchCategoryAttributes(service.key);
                              }}
                              sx={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                cursor: 'pointer',
                              }}
                            >
                              <Checkbox
                                checked={checked}
                                onChange={() => {
                                  setSelectedCategoryKey(service.key);
                                  setFormError('');
                                  props.fetchCategoryAttributes(service.key);
                                }}
                                sx={{
                                  color: isDark
                                    ? GlassColors.dark.textPrimary
                                    : GlassColors.light.textPrimary,
                                  '&.Mui-checked': {
                                    color: isDark ? '#D9D9D9' : '#2D2D2D',
                                  },
                                }}
                              />

                              <Box sx={{ flex: 1 }}>
                                <Typography
                                  sx={{
                                    fontFamily: 'Roboto, sans-serif',
                                    fontSize: { xs: '16px', md: '16px' },
                                    fontWeight: 700,
                                    lineHeight: 1.75,
                                    letterSpacing: '.15px',
                                    pt: '6px',
                                    color: isDark
                                      ? GlassColors.dark.textPrimary
                                      : GlassColors.light.textPrimary,
                                    mb: { xs: '8px', sm: '3px' },
                                  }}
                                >
                                  {service.name}
                                </Typography>

                                <Typography
                                  sx={{
                                    fontFamily: 'Roboto, sans-serif',
                                    fontSize: { xs: '14px', md: '16px' },
                                    fontWeight: 400,
                                    lineHeight: 1.5,
                                    letterSpacing: '.15px',
                                    color: isDark
                                      ? GlassColors.dark.textSecondary
                                      : GlassColors.light.textSecondary,
                                  }}
                                >
                                  {service.description ??
                                    'Description will be added soon.'}
                                </Typography>
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>

                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          mt: { xs: '16px', sm: 0 },
                        }}
                      >
                        <ButtonTertiary
                          variant='outlined'
                          onClick={() => {
                            setSelectedSubCategoryKey(null);
                            setSelectedCategoryKey(undefined);
                            setFormError('');
                            resetCategorySearch();
                          }}
                          sx={{
                            display: { xs: 'flex', sm: 'none' },
                            minWidth: 'unset',
                            px: 2,
                            py: 0.75,
                            borderRadius: '999px',
                            '& path': { fill: 'currentColor' },
                          }}
                        >
                          <ArrowBackIosIcon />
                          Back
                        </ButtonTertiary>
                        <GlassButton
                          onClick={handleNext}
                          disabled={createOrderLoading}
                          sx={{
                            opacity: selectedCategoryKey ? 1 : 0,
                            display: { xs: 'flex', sm: 'none' },

                            '&&': {
                              height: '32px',
                              fontSize: '12px',
                              transition: 'box-shadow 0.3s ease, opacity .3s',
                            },
                          }}
                        >
                          {createOrderLoading ? (
                            <CircularProgress size={24} color='inherit' />
                          ) : (
                            'Next'
                          )}
                        </GlassButton>
                      </Box>
                    </GlassCard>
                  </Box>
                )}
              </Box>
            )}

          {currentStep === CreateOrderStep.TASK_DESCRIPTION &&
            !showHandymanStage && (
              <Box sx={{ mb: 2 }}>
                <Grid container spacing={2}>
                  <Grid size={12}>
                    <FormControl fullWidth>
                      <FormLabel
                        htmlFor='taskName'
                        sx={{
                          fontFamily: 'roboto, sans-serif',
                          color: isDark
                            ? GlassColors.dark.textPrimary
                            : GlassColors.light.textPrimary,
                        }}
                      >
                        Task name*
                      </FormLabel>
                      <GlassTextField
                        fullWidth
                        id='taskName'
                        value={taskName}
                        onChange={(e) => setTaskName(e.target.value)}
                      />
                    </FormControl>
                  </Grid>
                  <Grid size={12}>
                    <FormControl fullWidth>
                      <FormLabel
                        htmlFor='taskDescription'
                        sx={{
                          fontFamily: 'roboto, sans-serif',
                          color: isDark
                            ? GlassColors.dark.textPrimary
                            : GlassColors.light.textPrimary,
                        }}
                      >
                        Task Description*
                      </FormLabel>
                      <GlassTextField
                        fullWidth
                        multiline
                        id='taskDescription'
                        minRows={4}
                        maxRows={10}
                        placeholder='Describe your task in detail. For example: "I need to assemble an IKEA Malm dresser. I have all the parts, but no tools. The location is on the 2nd floor with no elevator.'
                        value={taskDescription}
                        onChange={(e) => setTaskDescription(e.target.value)}
                        sx={{
                          '& .MuiInputBase-root': { minHeight: '108px' },
                          '& textarea': {
                            px: '12px',
                          },
                        }}
                      />
                    </FormControl>
                  </Grid>
                  {/* New fields: Due Date & Price */}
                  {/* <Grid size={{ xs: 12, sm: 6 }}>
                  <GlassTextField
                    fullWidth
                    label='Due Date'
                    type='datetime-local'
                    value={dueDate}
                    onChange={(e) => setDueDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{
                      max: '9999-12-31T23:59',
                    }}
                    sx={{ mb: 2 }}
                  />
                </Grid> */}
                  <Grid size={12} sx={{ display: 'flex', flexWrap: 'wrap' }}>
                    <Box
                      sx={{
                        width: {
                          xs: '100%',
                          sm: '50%',
                        },
                        mb: { xs: '16px', sm: 0 },
                      }}
                    >
                      <Typography
                        sx={{
                          mb: 1,
                          fontFamily: 'Roboto, sans-serif',
                          fontSize: '14px',
                          lineHeight: 1.75,
                          letterSpacing: '.15px',
                          fontWeight: 400,
                        }}
                      >
                        When do you need it done?
                      </Typography>

                      <Grid container sx={{ rowGap: '8px' }}>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <Box
                            onClick={() => setDueDateOption('specific-date')}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              cursor: 'pointer',
                              height: '28px',
                            }}
                          >
                            <Checkbox
                              checked={dueDateOption === 'specific-date'}
                              onChange={() => setDueDateOption('specific-date')}
                              sx={{
                                color: isDark
                                  ? GlassColors.dark.textPrimary
                                  : GlassColors.light.textPrimary,
                              }}
                            />
                            <Typography
                              sx={{
                                fontFamily: 'Roboto, sans-serif',
                                fontSize: '14px',
                                fontWeight: 400,
                                letterSpacing: '.1px',
                              }}
                            >
                              On a specific date
                            </Typography>
                          </Box>
                        </Grid>

                        <Grid size={{ xs: 12, sm: 6 }}>
                          <Box
                            onClick={() => setDueDateOption('this-week')}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              cursor: 'pointer',
                              height: '28px',
                            }}
                          >
                            <Checkbox
                              checked={dueDateOption === 'this-week'}
                              onChange={() => setDueDateOption('this-week')}
                              sx={{
                                color: isDark
                                  ? GlassColors.dark.textPrimary
                                  : GlassColors.light.textPrimary,
                              }}
                            />
                            <Typography
                              sx={{
                                fontFamily: 'Roboto, sans-serif',
                                fontSize: '14px',
                                fontWeight: 400,
                                letterSpacing: '.1px',
                              }}
                            >
                              Sometime this week
                            </Typography>
                          </Box>
                        </Grid>

                        <Grid size={{ xs: 12, sm: 6 }}>
                          <Box
                            onClick={() => setDueDateOption('within-3-days')}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              cursor: 'pointer',
                              height: '28px',
                            }}
                          >
                            <Checkbox
                              checked={dueDateOption === 'within-3-days'}
                              onChange={() => setDueDateOption('within-3-days')}
                              sx={{
                                color: isDark
                                  ? GlassColors.dark.textPrimary
                                  : GlassColors.light.textPrimary,
                              }}
                            />
                            <Typography
                              sx={{
                                fontFamily: 'Roboto, sans-serif',
                                fontSize: '14px',
                                fontWeight: 400,
                                letterSpacing: '.1px',
                              }}
                            >
                              Within the next 3 days
                            </Typography>
                          </Box>
                        </Grid>

                        <Grid size={{ xs: 12, sm: 6 }}>
                          <Box
                            onClick={() => setDueDateOption('flexible')}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              cursor: 'pointer',
                              height: '28px',
                            }}
                          >
                            <Checkbox
                              checked={dueDateOption === 'flexible'}
                              onChange={() => setDueDateOption('flexible')}
                              sx={{
                                color: isDark
                                  ? GlassColors.dark.textPrimary
                                  : GlassColors.light.textPrimary,
                              }}
                            />
                            <Typography
                              sx={{
                                fontFamily: 'Roboto, sans-serif',
                                fontSize: '14px',
                                fontWeight: 400,
                                letterSpacing: '.1px',
                              }}
                            >
                              I&apos;m flexible
                            </Typography>
                          </Box>
                        </Grid>

                        {dueDateOption === 'specific-date' && (
                          <Grid size={12}>
                            <FormControl fullWidth>
                              <FormLabel
                                htmlFor='price'
                                sx={{
                                  fontFamily: 'roboto, sans-serif',
                                  color: isDark
                                    ? GlassColors.dark.textPrimary
                                    : GlassColors.light.textPrimary,
                                  mt: 1,
                                }}
                              >
                                Select date
                              </FormLabel>
                              <GlassTextField
                                fullWidth
                                type='date'
                                value={specificDueDate}
                                onChange={(e) =>
                                  setSpecificDueDate(e.target.value)
                                }
                                InputLabelProps={{ shrink: true }}
                              />
                            </FormControl>
                          </Grid>
                        )}
                      </Grid>
                    </Box>
                    <Box
                      sx={{
                        width: {
                          xs: '100%',
                          sm: '50%',
                          pl: { xs: 0, sm: '10px' },
                        },
                      }}
                    >
                      <FormControl fullWidth>
                        <FormLabel
                          htmlFor='price'
                          sx={{
                            fontFamily: 'roboto, sans-serif',
                            color: isDark
                              ? GlassColors.dark.textPrimary
                              : GlassColors.light.textPrimary,
                          }}
                        >
                          Price
                        </FormLabel>
                        <GlassTextField
                          fullWidth
                          id='price'
                          type='number'
                          value={price}
                          onChange={(e) => setPrice(e.target.value)}
                        />
                      </FormControl>
                    </Box>
                  </Grid>
                  <Grid size={12}>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Autocomplete
                          onLoad={(autocomplete) =>
                            setAutocompleteRef(autocomplete)
                          }
                          onPlaceChanged={() => {
                            const place = autocompleteRef?.getPlace();
                            if (place?.formatted_address) {
                              setAddress(place.formatted_address);
                            }
                          }}
                        >
                          <FormControl fullWidth>
                            <FormLabel
                              htmlFor='address'
                              sx={{
                                fontFamily: 'roboto, sans-serif',
                                color: isDark
                                  ? GlassColors.dark.textPrimary
                                  : GlassColors.light.textPrimary,
                              }}
                            >
                              Address
                            </FormLabel>
                            <GlassTextField
                              fullWidth
                              id='address'
                              placeholder=''
                              value={address}
                              onChange={(e) => setAddress(e.target.value)}
                            />
                          </FormControl>
                        </Autocomplete>
                      </Grid>
                      {/* Image Upload Area */}
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Box
                          {...getRootProps()}
                          sx={{
                            border: '2px dashed #393939',
                            textAlign: 'center',
                            borderRadius: 3,
                            height: '100%',
                            minHeight: '64px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            '&:hover': {
                              cursor: 'pointer',
                            },
                          }}
                        >
                          <input {...getInputProps()} />
                          <Typography>
                            Attach photos or docs (1mb max file size)
                          </Typography>
                        </Box>
                      </Grid>
                      {photos.length > 0 && (
                        <Grid size={12}>
                          <Grid container>
                            {photos.map((photo, index) => (
                              <Grid key={index} size={{ xs: 4, sm: 3 }}>
                                <Box position='relative'>
                                  <img
                                    src={photo.src}
                                    alt={`Uploaded ${index}`}
                                    style={{
                                      width: '100%',
                                      height: 'auto',
                                      borderRadius: 4,
                                      cursor: 'pointer',
                                    }}
                                  />
                                  <IconButton
                                    color='error'
                                    size='small'
                                    onClick={() =>
                                      setPhotos((prev) =>
                                        prev.filter((_, i) => i !== index),
                                      )
                                    }
                                    sx={{
                                      position: 'absolute',
                                      top: 4,
                                      right: 4,
                                    }}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Box>
                              </Grid>
                            ))}
                          </Grid>
                        </Grid>
                      )}
                    </Grid>
                  </Grid>

                  {/* Render dynamic category attributes */}
                  {isFetchingAttributes && (
                    <Grid size={12}>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          my: 2,
                        }}
                      >
                        <CircularProgress />
                      </Box>
                    </Grid>
                  )}
                  {attributes && attributes.length > 0 && (
                    <>
                      <Grid size={12}>
                        <Typography variant='subtitle1'>
                          Additional Details
                        </Typography>
                      </Grid>
                      {attributes.map((attr) => (
                        <Grid size={12} key={attr.id}>
                          {renderAttributeInput(attr)}
                        </Grid>
                      ))}
                    </>
                  )}
                </Grid>
                {createOrderError && (
                  <Alert
                    severity='error'
                    sx={{ mt: 2, color: isDark ? '#red' : '#black' }}
                  >
                    {createOrderError}
                  </Alert>
                )}
              </Box>
            )}
          {/* Stepper Controls */}
          {!showHandymanStage && (
            <RailStepper
              isProviderFlow={true}
              activeStep={steps.indexOf(currentStep)}
              clickableUntil={steps.indexOf(currentStep)}
              isDark={isDark}
              onStepClick={(idx) => {
                if (!orderCreated) {
                  setCurrentStep(steps[idx]);
                  setFormError('');
                  resetCategorySearch();
                }
              }}
            />
          )}
          {!showHandymanStage &&
            currentStep === CreateOrderStep.TASK_DESCRIPTION && (
              <Box
                sx={{
                  mt: 4,
                  display: 'flex',
                  justifyContent: 'space-between',
                }}
              >
                <ButtonTertiary onClick={handleBack} variant='outlined'>
                  Back to category selection
                </ButtonTertiary>
                <Box>
                  <GlassButton
                    onClick={handleNext}
                    disabled={createOrderLoading}
                    sx={{ '&&': { fontSize: '12px', py: '4px' } }}
                  >
                    {createOrderLoading ? (
                      <CircularProgress size={24} color='inherit' />
                    ) : (
                      currentStep === CreateOrderStep.TASK_DESCRIPTION && (
                        <>
                          <Box sx={{ display: { xs: 'inline', sm: 'none' } }}>
                            Next
                          </Box>

                          <Box sx={{ display: { xs: 'none', sm: 'inline' } }}>
                            Next: Choose a Handyman
                          </Box>
                        </>
                      )
                    )}
                  </GlassButton>
                </Box>
              </Box>
            )}

          {showHandymanStage && (
            <Box>
              <Typography
                variant='h4'
                sx={{
                  m: { xs: '0 auto 16px', sm: '0 auto 24px' },
                  textAlign: 'center',
                  fontSize: '24px',
                  fontFamily: 'Montserrat, sans-serif',
                  fontWeight: 700,
                  maxWidth: '90%',
                  lineHeight: '133%',
                  color: isDark ? GlassColors.accent : GlassColors.secondary,
                }}
              >
                Browse Professionals (optional)
              </Typography>

              {orderCreated &&
                orderCreationNotification &&
                !shouldShowInviteResultOnly && (
                  <Box
                    sx={{
                      position: 'relative',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '16px',
                      p: '24px',
                      borderRadius: '32px',
                      backgroundColor: isDark ? '#393939' : '#D1D0C6',
                      mb: 3,
                      boxShadow: `
                      -2.82px -2.82px 8.49px 0px #FFFFFF4D,
                      14.15px 14.15px 22.64px -0.37px #0000001A,
                      -17.54px -17.54px 28.66px 0px #FFFFFF0D,
                      10px 10px 28.66px -11.7px #00000033,
                      1.58px 1.58px 1.7px 0px #FFFFFF inset,
                      -2.07px -2.64px 4.52px -1.51px #0000004D inset
                    `,
                    }}
                  >
                    <IconButton
                      onClick={() => setOrderCreationNotification(false)}
                      disableRipple
                      sx={{
                        p: 0,
                        width: 'auto',
                        height: 'auto',
                        border: 'none !important',
                        position: 'absolute',
                        top: '24px',
                        right: '24px',
                        '& svg path': {
                          fill: isDark
                            ? GlassColors.dark.textPrimary
                            : GlassColors.light.textPrimary,
                          transition: 'fill .3s',
                        },
                        background: 'transparent !important',
                        '&:hover': {
                          background: 'transparent !important',
                          '& svg path': {
                            fill: isDark
                              ? GlassColors.light.textPrimary
                              : '#fff',
                          },
                        },
                      }}
                    >
                      <CloseIcon />
                    </IconButton>
                    <Typography
                      sx={{
                        fontFamily: 'Montserrat, sans-serif',
                        fontSize: '20px',
                        fontWeight: 800,
                        lineHeight: 1.6,
                        letterSpacing: '.15px',
                        pr: '60px',
                        color: isDark
                          ? GlassColors.accent
                          : GlassColors.secondary,
                      }}
                    >
                      {inviteStatus === 'failed'
                        ? 'Task Created, but Invitation Was Not Sent'
                        : 'Success! Your Task is Live'}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: 'Roboto, sans-serif',
                        fontSize: '16px',
                        lineHeight: 1.5,
                        letterSpacing: '.15px',
                        color: isDark
                          ? GlassColors.dark.textSecondary
                          : GlassColors.light.textSecondary,
                        '& a': {
                          fontWeight: 700,
                          color: isDark
                            ? GlassColors.accent
                            : GlassColors.secondary,
                          textDecoration: 'none',
                          position: 'relative',
                          '&:after': {
                            content: '""',
                            display: 'block',
                            width: '100%',
                            height: '1px',
                            position: 'absolute',
                            bottom: '0px',
                            left: 0,
                            backgroundColor: isDark
                              ? GlassColors.accent
                              : GlassColors.secondary,
                            transition: 'width .3s',
                          },
                          '&:hover::after': { width: 0 },
                        },
                      }}
                    >
                      Your task request is now active and you can find it in "
                      <Link to='/orders'>My Tasks</Link>". Our system is already
                      searching for a match. For faster results or to choose a
                      specific professional, you can browse profiles below and
                      send a proposal directly
                    </Typography>
                  </Box>
                )}

              {shouldShowInviteResultOnly && (
                <Box
                  sx={{
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '16px',
                    p: '24px',
                    borderRadius: '32px',
                    backgroundColor: isDark ? '#393939' : '#D1D0C6',
                    mb: 3,
                    boxShadow: `
        -2.82px -2.82px 8.49px 0px #FFFFFF4D,
        14.15px 14.15px 22.64px -0.37px #0000001A,
        -17.54px -17.54px 28.66px 0px #FFFFFF0D,
        10px 10px 28.66px -11.7px #00000033,
        1.58px 1.58px 1.7px 0px #FFFFFF inset,
        -2.07px -2.64px 4.52px -1.51px #0000004D inset
      `,
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: 'Montserrat, sans-serif',
                      fontSize: '20px',
                      fontWeight: 800,
                      lineHeight: 1.6,
                      letterSpacing: '.15px',
                      pr: '60px',
                      color: isDark
                        ? GlassColors.accent
                        : GlassColors.secondary,
                    }}
                  >
                    {`Proposal Sent to ${handyman.name}!`}
                  </Typography>

                  <Typography
                    sx={{
                      fontFamily: 'Roboto, sans-serif',
                      fontSize: '16px',
                      lineHeight: 1.5,
                      letterSpacing: '.15px',
                      color: isDark
                        ? GlassColors.dark.textSecondary
                        : GlassColors.light.textSecondary,
                      '& a': {
                        fontWeight: 700,
                        color: isDark
                          ? GlassColors.accent
                          : GlassColors.secondary,
                        textDecoration: 'none',
                        position: 'relative',
                        '&:after': {
                          content: '""',
                          display: 'block',
                          width: '100%',
                          height: '1px',
                          position: 'absolute',
                          bottom: '0px',
                          left: 0,
                          backgroundColor: isDark
                            ? GlassColors.accent
                            : GlassColors.secondary,
                          transition: 'width .3s',
                        },
                        '&:hover::after': { width: 0 },
                      },
                    }}
                  >
                    {`Your task details have been sent directly to ${handyman.name} for their review. You will receive a notification
                    as soon as they respond or accept the task. `}
                    In the meantime, you can track the status of your proposal
                    in your "<Link to='/orders'>My Tasks</Link>" tab.
                  </Typography>

                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      mt: '8px',
                    }}
                  ></Box>
                </Box>
              )}
              {shouldShowBrowseHandyman && (
                <GoogleMapsWrapper fallback={<BrowseHandymanSkeleton />}>
                  <BrowseHandymanList
                    preselectedCategoryKey={selectedCategoryKey}
                    excludeFilters={['price-range', 'name', 'categories']}
                    address={address || handymanFilterAddress}
                    radiusKm={handymanRadiusKm}
                    actionButtonTitle={getActionBtnTitle}
                    onAction={handleBrowseHandymanAction}
                    isNotHandyman={true}
                    needFilters={false}
                    sentProposalIds={sentProposalIds}
                  />
                </GoogleMapsWrapper>
              )}
              {/* )} */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mt: '8px',
                }}
              >
                <ButtonTertiary
                  onClick={() => navigate('/orders')}
                  sx={{
                    '&&': {
                      fontSize: { xs: '12px', sm: '16px' },
                      fontWeight: 500,
                      padding: { xs: '4px 12px', sm: '10px 24px' },
                    },
                  }}
                >
                  Go to My Tasks
                </ButtonTertiary>
                <NeumorphicButton
                  onClick={() => handleReset()}
                  sx={{
                    padding: { xs: '4px 12px', sm: '4px 24px' },
                    height: { xs: 'auto', sm: '48px' },
                  }}
                >
                  Create Another Task
                </NeumorphicButton>
              </Box>
            </Box>
          )}
        </GlassCard>
      </Box>
    </GlassContainer>
  );
};

const mapStateToProps = (state: any) => ({
  attributes: state.order.attributes,
  isFetchingAttributes: state.order.isFetchingAttributes,
  createOrderLoading: state.order.createOrderLoading,
  createOrderError: state.order.createOrderError,
  createdOrder: state.order.createdOrder,
  allCategories: state.category.allCategories,
  isFetchingCategories: state.category.isLoading,
  categoriesError: state.category.isError ? 'Failed to load categories' : null,
});

export default connect(mapStateToProps, {
  fetchCategoryAttributes,
  createOrder,
  sendOrderProposal,
  getAllCategories,
  clearCreatedOrder,
})(CreateOrderPage);
